import { equals, uniqWith } from 'ramda';
import type {
  InsertNodeOperation,
  MergeNodeOperation,
  MoveNodeOperation,
  Operation,
  RemoveNodeOperation,
  SetNodeOperation,
  SetSelectionOperation,
  SplitNodeOperation,
} from 'slate';
import { Editor, Node, Path, Range, Text } from 'slate';

import type { UUID } from '../../types/type-helpers';
import type {
  DocumentElement,
  DynamicBlockElement,
  ListLevel,
  ParagraphProps,
  SuggestedEdit,
} from '../internal';
import {
  isDocumentElement,
  isDynamicBlockElement,
  isListItemEdit,
  isListLevel,
  isPageDimensionsEdit,
  isParagraphElement,
  Queries,
} from '../internal';

/**
 * Utility function to deduplicate paths using Path.equals for comparison
 */
function deduplicatePaths(paths: Path[]): Path[] {
  return uniqWith(Path.equals, paths);
}

/**
 * Adjacent paragraph siblings whose cached border-merge / spacing data depends on `path`.
 * Paragraph RM nodes precompute merges with both neighbors, so both must be invalidated.
 */
function getAdjacentParagraphNeighborPaths(editor: Editor, path: Path): Path[] {
  const neighborPaths: Path[] = [];

  if (Path.hasPrevious(path)) {
    const previousPath = Path.previous(path);
    const previousNode = Node.getIf(editor, previousPath);
    if (previousNode && isParagraphElement(previousNode)) neighborPaths.push(previousPath);
  }

  const nextPath = Path.next(path);
  const nextNode = Node.getIf(editor, nextPath);
  if (nextNode && isParagraphElement(nextNode)) neighborPaths.push(nextPath);

  return neighborPaths;
}

export function getDirtyPaths(editor: Editor, op: Operation): Path[] {
  switch (op.type) {
    case 'insert_text':
    case 'remove_text': {
      return Path.levels(op.path);
    }
    case 'set_node': {
      const { path } = op;
      const levels = Path.levels(path);
      const { impactedPaths: changeListDefinitionsPaths, node } = getSetNodeImpactedListItemPaths(
        editor,
        op
      );

      const allPaths = [...levels, ...changeListDefinitionsPaths];
      if (!isParagraphElement(node)) return deduplicatePaths(allPaths);

      allPaths.push(...getAdjacentParagraphNeighborPaths(editor, path));

      return deduplicatePaths(allPaths);
    }

    case 'insert_node': {
      const { node, path } = op;
      const levels = Path.levels(path);
      const descendants: Path[] = !Text.isText(node)
        ? [...Node.nodes(node)].map(([, p]) => path.concat(p))
        : [];
      const { impactedListItemPaths } = getImpactedListItemPaths(editor, op);

      // Use uniq instead of Set operations for deduplication
      const allPaths = [...levels, ...descendants, ...impactedListItemPaths];

      if (!isParagraphElement(node)) return deduplicatePaths(allPaths);

      allPaths.push(...getAdjacentParagraphNeighborPaths(editor, path));

      return deduplicatePaths(allPaths);
    }

    case 'merge_node': {
      const { path } = op;
      const ancestors = Path.ancestors(path);
      // merge_node always has a previous sibling (the merge target).
      const previousPath = Path.previous(path);
      const nextPath = Path.next(path);
      const { impactedListItemPaths } = getImpactedListItemPaths(editor, op);

      // Always dirty previous + next for structural merge; previous covers border-merge cache.
      return deduplicatePaths([...ancestors, ...impactedListItemPaths, previousPath, nextPath]);
    }

    case 'move_node': {
      const { newPath, path } = op;
      const oldAncestors: Path[] = [];
      const newAncestors: Path[] = [];
      const { impactedListItemPaths } = getImpactedListItemPaths(editor, op);

      if (Path.equals(path, newPath)) return [];

      for (const ancestor of Path.ancestors(path)) {
        const p = Path.transform(ancestor, op);
        if (!p) continue;

        oldAncestors.push(p);
      }

      for (const ancestor of Path.ancestors(newPath)) {
        const p = Path.transform(ancestor, op);
        if (!p) continue;

        newAncestors.push(p);
      }

      const dirtyPaths = [
        ...oldAncestors,
        ...newAncestors,
        ...impactedListItemPaths,
        newPath,
        ...getAdjacentParagraphNeighborPaths(editor, path),
        ...getAdjacentParagraphNeighborPaths(editor, newPath),
      ];

      return deduplicatePaths(dirtyPaths);
    }

    case 'remove_node': {
      const { path } = op;
      const nextPath = Path.next(path);
      const ancestors = Path.ancestors(path);
      const { impactedListItemPaths, node } = getImpactedListItemPaths(editor, op);

      const dirtyPaths = [...ancestors, ...impactedListItemPaths, nextPath];
      if (isParagraphElement(node))
        dirtyPaths.push(...getAdjacentParagraphNeighborPaths(editor, path));

      return deduplicatePaths(dirtyPaths);
    }

    case 'split_node': {
      const { path } = op;
      const levels = Path.levels(path);
      const nextPath = Path.next(path);
      const { impactedListItemPaths, node } = getImpactedListItemPaths(editor, op);
      const dirtyPaths = [...levels, ...impactedListItemPaths, nextPath];
      if (isParagraphElement(node))
        dirtyPaths.push(...getAdjacentParagraphNeighborPaths(editor, path));

      return deduplicatePaths(dirtyPaths);
    }
    case 'set_selection': {
      return deduplicatePaths(getAffectedCollapsedDCPaths(editor, op));
    }
  }
}

/**
 * "Collapsed" DCs can become collapsed/un-collapsed by virtue of the selection moving into/out of the DC.
 * This func returns the affected paths for such DCs.
 */
export function getAffectedCollapsedDCPaths(editor: Editor, op: SetSelectionOperation): Path[] {
  const { newProperties } = op;
  const paths: Path[] = [];
  const oldEmptyDynamicBlockEntry = Editor.above<DynamicBlockElement>(editor, {
    match: n => isDynamicBlockElement(n) && Queries.nodeIsEmpty(n),
    voids: true,
  });
  if (oldEmptyDynamicBlockEntry) {
    const [, oldEmptyDynamicBlockPath] = oldEmptyDynamicBlockEntry;
    paths.push(...Path.levels(oldEmptyDynamicBlockPath));
  }

  const safeSel = editor.selection ?? {};
  const newSel = newProperties ? { ...safeSel, ...newProperties } : null;
  const newEmptyDynamicBlockEntry = Range.isRange(newSel)
    ? Editor.above<DynamicBlockElement>(editor, {
        at: newSel,
        match: n => isDynamicBlockElement(n) && Queries.nodeIsEmpty(n),
        voids: true,
      })
    : undefined;
  if (newEmptyDynamicBlockEntry) {
    const [, newEmptyDynamicBlockPath] = newEmptyDynamicBlockEntry;
    paths.push(...Path.levels(newEmptyDynamicBlockPath));
  }

  return paths;
}

/**
 * Utility function to check if a set_node op changes page dimensions on the document.  Takes into account suggested edits.
 * Note: Order of the if statements in the function is important.  Be careful changing.
 */
export function opChangesPageDimensions(editor: Editor, op: SetNodeOperation): boolean {
  const node = Node.get(editor, op.path);
  if (!isDocumentElement(node)) return false;

  if ('pageDimensions' in op.newProperties) return true;

  if ('suggestedEdits' in op.newProperties) {
    const newPropsContainsPageDimensionsEdit = op.newProperties.suggestedEdits?.some(
      (suggestedEdit: SuggestedEdit): boolean => isPageDimensionsEdit(suggestedEdit)
    );

    // If there's a suggested page dimensions edit in op.newProperties.suggestedEdits, page dimensions has changed.
    if (newPropsContainsPageDimensionsEdit) return true;

    /**
     * If we've gotten here we know op.newProperties.suggestedEdits doesn't contain a page dimensions edit.  But, op.properties.suggestedEdits
     * could contain a suggested page dimensions edit so we check that.
     */
    if ('suggestedEdits' in op.properties) {
      const propsContainsPageDimensionsEdit = op.properties.suggestedEdits?.some(
        (suggestedEdit: SuggestedEdit): boolean => isPageDimensionsEdit(suggestedEdit)
      );

      /**
       * op.properties.suggestedEdits contains a page dimensions edit and op.newProperties.suggestedEdits does not, indicating a
       * user rejected a page dimensions edit.
       */
      if (propsContainsPageDimensionsEdit) return true;
    }
  }

  return false;
}

interface SetMarkerFormatsOp extends SetNodeOperation {
  newProperties: Pick<DocumentElement, 'listDefinitions'>;
  properties: Partial<Pick<DocumentElement, 'listDefinitions'>>;
}

export function isOpListMarkerFormatsEdit(op: SetNodeOperation): op is SetMarkerFormatsOp {
  const listDefinitionsKey: keyof DocumentElement = 'listDefinitions';
  return listDefinitionsKey in op.newProperties;
}

/**
 * Utility function to check if a set_node op changes marker formats in list definitions.
 * Returns the affected paths, if any.
 */
export function getSetNodeImpactedListItemPaths(
  editor: Editor,
  op: SetNodeOperation
): { impactedPaths: Path[]; node: Node } {
  const node = Node.get(editor, op.path);

  if (isParagraphElement(node)) {
    const impactedListIds = new Set<string>();
    const activeListProps = Queries.renderedParagraphListProps(node);
    const listPropsKey: keyof ParagraphProps = 'listProps';
    const suggestedEditsKey: keyof ParagraphProps = 'suggestedEdits';
    // Slate strips cleared keys from `newProperties` (they survive only in `properties`),
    // so we check both sides to catch list removals.
    const listItemEdit = listPropsKey in op.newProperties ? op.newProperties.listProps : undefined;
    const removedListProps =
      listPropsKey in op.properties && !(listPropsKey in op.newProperties)
        ? op.properties.listProps
        : undefined;
    if (listPropsKey in op.newProperties || listPropsKey in op.properties) {
      if (listItemEdit) impactedListIds.add(listItemEdit.listId);
      if (activeListProps) impactedListIds.add(activeListProps.listId);
      if (removedListProps) impactedListIds.add(removedListProps.listId);
    }

    const suggestedListItemEdit =
      suggestedEditsKey in op.newProperties
        ? op.newProperties.suggestedEdits?.find(isListItemEdit)
        : null;
    if (suggestedListItemEdit && isListItemEdit(suggestedListItemEdit)) {
      const { listProps: suggestedListProps } = suggestedListItemEdit.value;

      if (activeListProps) impactedListIds.add(activeListProps.listId);
      if (suggestedListProps) impactedListIds.add(suggestedListProps.listId);
    }

    // Bail if the op doesn't affect a list item...
    if (!impactedListIds.size) return { impactedPaths: [], node };

    const impactedListItemEntries = [
      ...Editor.nodes(editor, {
        at: [],
        match: node => {
          if (!isParagraphElement(node)) return false;

          const nodeListProps = Queries.renderedParagraphListProps(node);
          if (!nodeListProps) return false;

          return [...impactedListIds].some(listId => listId === nodeListProps.listId);
        },
      }),
    ];

    return {
      impactedPaths: impactedListItemEntries.flatMap(([, path]) => Path.levels(path)),
      node,
    };
  }

  if (isDocumentElement(node) && isOpListMarkerFormatsEdit(op)) {
    const changedListDefinitions = Object.entries(op.newProperties.listDefinitions).reduce<
      Record<UUID, ListLevel[]>
    >((acc, [listId, listDefinition]) => {
      const changedLevels = Object.entries(listDefinition).reduce<ListLevel[]>(
        (acc, [levelString, listDefinition]) => {
          const listLevel = parseInt(levelString);
          if (!isListLevel(listLevel)) return acc;

          const { markerFormats: newMarkerFormats = {} } = listDefinition;
          const { markerFormats: oldMarkerFormats = {} } =
            op.properties.listDefinitions?.[listId]?.[listLevel] ?? {};
          if (equals(newMarkerFormats, oldMarkerFormats)) return acc;

          acc.push(listLevel);
          return acc;
        },
        []
      );

      acc[listId] = changedLevels;
      return acc;
    }, {});
    const impactedListItemEntries = [
      ...Editor.nodes(editor, {
        at: [],
        match: node => {
          if (!isParagraphElement(node)) return false;

          const nodeListProps = Queries.renderedParagraphListProps(node);
          if (!nodeListProps) return false;

          const changedDefinitionLevels = changedListDefinitions[nodeListProps.listId];
          if (!changedDefinitionLevels) return false;

          return changedDefinitionLevels.some(level => level === nodeListProps.listLevel);
        },
      }),
    ];

    return {
      impactedPaths: impactedListItemEntries.flatMap(([, path]) => Path.levels(path)),
      node,
    };
  }

  return { impactedPaths: [], node };
}

/**
 * Gets the paths of list items affected by the given Operation.
 */
export function getImpactedListItemPaths(
  editor: Editor,
  op:
    | InsertNodeOperation
    | RemoveNodeOperation
    | SplitNodeOperation
    | MoveNodeOperation
    | MergeNodeOperation
): { impactedListItemPaths: Path[]; node: Node } {
  const impactedNode = op.type === 'insert_node' ? op.node : Node.get(editor, op.path);
  if (!isParagraphElement(impactedNode)) return { impactedListItemPaths: [], node: impactedNode };

  const activeListProps = Queries.renderedParagraphListProps(impactedNode);
  if (!activeListProps) return { impactedListItemPaths: [], node: impactedNode };

  const impactedListItemEntries = [
    ...Editor.nodes(editor, {
      at: [],
      match: node => {
        if (!isParagraphElement(node)) return false;

        const nodeListProps = Queries.renderedParagraphListProps(node);
        if (!nodeListProps) return false;

        return nodeListProps.listId === activeListProps.listId;
      },
    }),
  ];
  const impactedListItemPaths = impactedListItemEntries.flatMap(([, path]) => {
    // Since all the ops in the type definition above could alter the current list item Path, we must transform it first before the op is applied.
    return Path.levels(Path.transform(path, op) ?? path);
  });

  return { impactedListItemPaths, node: impactedNode };
}
